import { useEffect } from 'react';
import templateWizardFormDefaults from '../../../constants/templateWizardFormDefaults';
import ArrowsStyles from './ArrowsStyles';
import BlockStyles from './BlockStyles';
import BottomCallToActionForm from './BottomCallToActionForm';
import DiscountBadgeForm from './DiscountBadgeForm';
import GeneralStylesForm from './GeneralStylesForm';
import ProductNameForm from './ProductNameForm';
import ProductPriceForm from './ProductPriceForm';
import TitleForm from './TitleForm';
import TopCallToActionForm from './TopCallToActionForm';

const WizardForm = ({ template, data, setData, disabled }) => {
  // backward compatibility - use useEffect to avoid setState during render.
  // setData here is not React's state setter: TemplateForm hands the argument
  // straight to transformWizardStyles, so it must be the plain wizardFormData
  // object - an updater function would be transformed as if it were the data.
  useEffect(() => {
    if (!data.topCallToActionStyles || !data.bottomCallToActionStyles) {
      // The fill has to match the guard: with ?? a falsy-but-not-nullish section
      // ('' from a hand-written api post) would survive, the guard would stay
      // true, and setData's new data identity would re-run this effect forever.
      setData({
        ...data,
        topCallToActionStyles:
          data.topCallToActionStyles || structuredClone(templateWizardFormDefaults.topCallToActionStyles),
        bottomCallToActionStyles:
          data.bottomCallToActionStyles || structuredClone(templateWizardFormDefaults.bottomCallToActionStyles),
      });
    }
  }, [data, setData]);

  // Don't render until backward compatibility defaults are applied
  if (!data.topCallToActionStyles || !data.bottomCallToActionStyles) {
    return null;
  }

  const transformColorToEvent = (field, color) => ({
    target: {
      name: field,
      value: color,
    },
  });

  const handleChangeStylesWrapper = (event, field) => {
    if (event) {
      setData({ ...data, [field]: { ...data[field], [event.target.name]: event.target.value } });
    }
  };

  const handleColorChangeWrapper = (field, color, styleField) => {
    handleChangeStylesWrapper(transformColorToEvent(field, color), styleField);
  };

  const renderGeneralStylesForm = () => (
    <GeneralStylesForm
      disabled={disabled}
      handleChangeStyles={(event) => handleChangeStylesWrapper(event, 'generalFontStyles')}
      productBlockStyles={data.generalFontStyles}
      title="General"
    />
  );

  const renderBlockStyles = () => (
    <BlockStyles
      disabled={disabled}
      handleChangeColors={(field, color) => handleColorChangeWrapper(field, color, 'blockStyles')}
      handleChangeStyles={(event) => handleChangeStylesWrapper(event, 'blockStyles')}
      productBlockStyles={data.blockStyles}
    />
  );

  const renderTopCallToActionForm = () => (
    <TopCallToActionForm
      disabled={disabled}
      handleChangeStyles={(event) => handleChangeStylesWrapper(event, 'topCallToActionStyles')}
      handleChangeColors={(field, color) => handleColorChangeWrapper(field, color, 'topCallToActionStyles')}
      productBlockStyles={data.topCallToActionStyles}
    />
  );

  const renderBottomallToActionForm = () => (
    <BottomCallToActionForm
      disabled={disabled}
      handleChangeStyles={(event) => handleChangeStylesWrapper(event, 'bottomCallToActionStyles')}
      handleChangeColors={(field, color) => handleColorChangeWrapper(field, color, 'bottomCallToActionStyles')}
      productBlockStyles={data.bottomCallToActionStyles}
    />
  );

  const renderTitleForm = () => (
    <TitleForm
      disabled={disabled}
      handleChangeStyles={(event) => handleChangeStylesWrapper(event, 'titleStyles')}
      handleChangeColors={(field, color) => handleColorChangeWrapper(field, color, 'titleStyles')}
      productBlockStyles={data.titleStyles}
    />
  );

  const renderArrowsStyles = () => (
    <ArrowsStyles
      disabled={disabled}
      handleChangeStyles={(event) => handleChangeStylesWrapper(event, 'arrowsStyles')}
      handleChangeColors={(field, color) => handleColorChangeWrapper(field, color, 'arrowsStyles')}
      productBlockStyles={data.arrowsStyles}
    />
  );

  const renderDiscountBadgeForm = () => (
    <DiscountBadgeForm
      disabled={disabled}
      handleChangeStyles={(event) => handleChangeStylesWrapper(event, 'discountBadgeStyles')}
      handleChangeColors={(field, color) => handleColorChangeWrapper(field, color, 'discountBadgeStyles')}
      productBlockStyles={data.discountBadgeStyles}
      showPosition={template.scope === 'web'}
    />
  );

  const renderProductNameForm = () => (
    <ProductNameForm
      disabled={disabled}
      handleChangeStyles={(event) => handleChangeStylesWrapper(event, 'productNameStyles')}
      handleChangeColors={(field, color) => handleColorChangeWrapper(field, color, 'productNameStyles')}
      productBlockStyles={data.productNameStyles}
    />
  );

  const renderProductPriceForm = (title, stylesField) => (
    <ProductPriceForm
      disabled={disabled}
      handleChangeStyles={(event) => handleChangeStylesWrapper(event, stylesField)}
      handleChangeColors={(field, color) => handleColorChangeWrapper(field, color, stylesField)}
      productBlockStyles={data.productPriceStyles}
      title={title}
    />
  );

  return (
    <>
      {renderGeneralStylesForm()}
      {template.scope === 'web' && (
        <>
          {renderBlockStyles()}
          {renderTopCallToActionForm()}
          {renderTitleForm()}
          {renderArrowsStyles()}
          {renderBottomallToActionForm()}
        </>
      )}
      {renderDiscountBadgeForm()}
      {renderProductNameForm()}
      {renderProductPriceForm('Product Price', 'productPriceStyles')}
      {renderProductPriceForm('Product List Price', 'productPriceDiscountedStyles')}
      {renderProductPriceForm('Product Discount Price', 'productDiscountPriceStyles')}
    </>
  );
};

export default WizardForm;
